import { useCallback, useEffect, useState } from 'react'
import { loadProductCatalog, type ProductCategory } from '@/api/productCatalog'

type Loaded = {
  key: string
  categories: ProductCategory[] | null
  error: unknown
}

export type ProductCatalogList = {
  categories: ProductCategory[]
  loading: boolean
  error: unknown
  /** Whether retired categories and interests are included. Off is what the picker sees. */
  includeInactive: boolean
  setIncludeInactive: (include: boolean) => void
  reload: () => void
}

/**
 * The catalogue arrives whole, so there is no paging and no search here — filtering a list of a
 * few dozen entries is something the page does in memory, without a round trip.
 */
export function useProductCatalog(initialIncludeInactive = false): ProductCatalogList {
  const [includeInactive, setIncludeInactiveState] = useState(initialIncludeInactive)
  const [reloadToken, setReloadToken] = useState(0)
  const [loaded, setLoaded] = useState<Loaded>({ key: '', categories: null, error: null })

  const requestKey = JSON.stringify([includeInactive, reloadToken])
  const loading = loaded.key !== requestKey

  useEffect(() => {
    const controller = new AbortController()

    loadProductCatalog(includeInactive, controller.signal)
      .then((catalog) => {
        if (!controller.signal.aborted) {
          setLoaded({ key: requestKey, categories: catalog.categories, error: null })
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoaded({ key: requestKey, categories: null, error })
        }
      })

    return () => controller.abort()
  }, [requestKey, includeInactive])

  const setIncludeInactive = useCallback((include: boolean) => {
    setIncludeInactiveState(include)
  }, [])

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  return {
    // Kept while the next request is in flight, so toggling the filter does not blank the page.
    categories: loaded.categories ?? [],
    loading,
    error: loaded.error,
    includeInactive,
    setIncludeInactive,
    reload,
  }
}
